import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {animate, state, transition, trigger, style} from '@angular/animations';
import {AbstractComponent} from "@components";
import {Country, EventItem, FetchEventByIdRequest, Organiser, PhoneNumber, TOAST_STATE} from "@model";
import {EventService, ToastService} from "@services";
import {take} from 'rxjs';
import {OrganiserFormMode} from '../../../organisers/organiser-details/organiser-details.component';
import {required} from '@utility';

@Component({
    selector: 'app-event-info',
    templateUrl: './event-info.component.html',
    styleUrls: ['./event-info.component.scss'],
    animations: [
        trigger('fadeInAnimation', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(1600)
            ])
        ])
    ],
    standalone: false
})
export class EventInfoComponent extends AbstractComponent implements OnInit, OnChanges {

  public static readonly DESCRIPTION_MAX_SIZE_MB=0.2;
  @Input() disableEditing: boolean = false;
  @Input() measurementByStotinka: boolean | undefined;
  @Input() organiserFormMode: OrganiserFormMode | undefined;
  @Input() currentOrganiser: Organiser | undefined;
  @Input() countries: Country[] = [];

  @Input() currentEventItem: EventItem | undefined;
  isDescriptionCollapsed = false;
  isRulesCollapsed = false;
  isPriseFundCollapsed = false;
  isContactInformationCollapsed = false;
  isHorizontalVideoCollapsed = false;
  isVerticalVideoCollapsed = false;
  selectedImage: string | undefined;

  currentPhoneNumbers: PhoneNumber[] = [];
  organisers: Organiser[] = [];
  isAdmin: boolean = false;
  mediaVideoHor: string | undefined;
  mediaVideoVert: string | undefined;

  inputForm = new FormGroup({
    description: new FormControl(),
    descriptionEng: new FormControl(),
    descriptionIta: new FormControl(),
    descriptionSlo: new FormControl(),
    descriptionSr: new FormControl(),
    descriptionBs: new FormControl(),
    rules: new FormControl(),
    rulesEng: new FormControl(),
    rulesIta: new FormControl(),
    rulesSlo: new FormControl(),
    rulesSr: new FormControl(),
    rulesBs: new FormControl(),
    awardFund: new FormControl(),
    awardFundEng: new FormControl(),
    awardFundIta: new FormControl(),
    awardFundSlo: new FormControl(),
    awardFundSr: new FormControl(),
    awardFundBs: new FormControl(),
    imageMedium: new FormControl(),
    rulesImage: new FormControl(),
    descriptionImage: new FormControl(),
    eMail: new FormControl(),
    phone: new FormControl(),
    website: new FormControl(),
    measurementByStotinka: new FormControl(),
    contactName: new FormControl(),
    contactNameEng: new FormControl(),
    contactNameSlo: new FormControl(),
    contactNameIta: new FormControl(),
    contactNameSr: new FormControl(),
    contactNameBs: new FormControl(),
    defaultLanguage: new FormControl(),
    extraLanguages: new FormControl(),
    mediaContentVideoVert: new FormControl(),
    mediaContentVideoHor: new FormControl(),
    mediaContentHor: new FormControl(),
    mediaContentHorEng: new FormControl(),
    mediaContentHorIta: new FormControl(),
    mediaContentHorSlo: new FormControl(),
    mediaContentHorSr: new FormControl(),
    mediaContentHorBs: new FormControl(),
    mediaContentVert: new FormControl(),
    mediaContentVertEng: new FormControl(),
    mediaContentVertIta: new FormControl(),
    mediaContentVertSlo: new FormControl(),
    mediaContentVertSr: new FormControl(),
    mediaContentVertBs: new FormControl(),
    phones: this.formBuilder.array([])
  })

  constructor(
    public translocoService: TranslocoService,
    private router: Router,
    private eventService: EventService,
    private toastService: ToastService,
    public formBuilder: FormBuilder) {
    super();
  }

  ngOnInit() {
    this.initForm();
    this.setFormValues();
    this.inputForm.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.inputForm.updateValueAndValidity();
    this.inputForm.get("mediaContentVideoHor")?.valueChanges.subscribe(x => {
      this.mediaVideoHor = 'https://www.youtube.com/embed/' + this.makeLinkEmbeddable(x);
      this.videoHorLinkIsValid();
    })
    this.inputForm.get("mediaContentVideoVert")?.valueChanges.subscribe(x => {
      this.mediaVideoVert = 'https://www.youtube.com/embed/' + this.makeLinkEmbeddable(x);
      this.videoVertLinkIsValid();
    })
  }

  makeLinkEmbeddable(link: string | undefined): string | undefined {
    const regex1: RegExp = /[?&]v=([^&#]*)/;
    const regex2: RegExp = /\/shorts\/([^\/?]+)/;
    const match1: RegExpMatchArray|null|undefined = link?.match(regex1);
    const match2: RegExpMatchArray|null|undefined = link?.match(regex2);
    if (match1) {
      return match1 ? match1[1] : undefined;
    }
    if (match2) {
      return match2 ? match2[1] : undefined;
    }
    return undefined;
  }

  videoHorLinkIsValid(): boolean {
    var p = /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
    return !!this.mediaVideoHor?.match(p);
  }

  videoVertLinkIsValid(): boolean {
    var p = /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
    return !!this.mediaVideoVert?.match(p);
  }

  private initForm() {
    if (this.currentEventItem) {
      this.inputForm.patchValue(this.currentEventItem)
    }
    if (this.makeLinkEmbeddable(this.currentEventItem?.mediaContentVideoHor) === undefined) {
      this.mediaVideoHor = '';
    } else {
      this.mediaVideoHor = 'https://www.youtube.com/embed/' + this.makeLinkEmbeddable(this.currentEventItem?.mediaContentVideoHor)
    }
    if (this.makeLinkEmbeddable(this.currentEventItem?.mediaContentVideoVert) === undefined) {
      this.mediaVideoVert = '';
    } else {
      this.mediaVideoVert = 'https://www.youtube.com/embed/' + this.makeLinkEmbeddable(this.currentEventItem?.mediaContentVideoVert);
    }
    this.inputForm.controls.mediaContentVideoHor.setValue(this.mediaVideoHor);
    this.inputForm.controls.mediaContentVideoVert.setValue(this.mediaVideoVert);
    if (this.currentEventItem?.infoEmail) {
      this.inputForm.controls.eMail.setValue(this.currentEventItem?.infoEmail);
    } else {
      this.inputForm.controls.eMail.setValue(this.currentEventItem?.organiser?.email);
    }
    this.inputForm.controls.phones = this.formBuilder.array([]);

    this.currentPhoneNumbers = this.currentEventItem?.phoneNumbers ? [...this.currentEventItem?.phoneNumbers] : [];

    if (this.currentPhoneNumbers.length === 0) {
      // No saved phone numbers at all — fall back to the organiser's own phone.
      let phoneNumber = this.formBuilder.group({
        phone: ['', required],
        contactName: ['', required],
        contactNameEng: [''],
        contactNameIta: [''],
        contactNameSlo: [''],
        contactNameSr: [''],
        contactNameBs: ['']
      });
      this.subscriptions.push(phoneNumber.valueChanges.subscribe(val => {
        this.inputForm.updateValueAndValidity();
      }));
      this.phones.push(phoneNumber);
      this.setPhoneNumber(this.currentEventItem?.organiser?.phone);
    } else {
      for (let i: number = 0; i < this.currentPhoneNumbers.length; i++) {
        let phoneNumber = this.formBuilder.group({
          phone: ['', required],
          contactName: ['', required],
          contactNameEng: [''],
          contactNameIta: [''],
          contactNameSlo: [''],
          contactNameSr: [''],
          contactNameBs: ['']
        });
        this.subscriptions.push(phoneNumber.valueChanges.subscribe(_ => {
          this.inputForm.updateValueAndValidity();
        }));
        this.phones.push(phoneNumber);
        if (this.currentPhoneNumbers[i]) {
          this.phones.at(i).get('phone')?.setValue(this.currentPhoneNumbers[i].phone);
          this.phones.at(i).get('contactName')?.setValue(this.currentPhoneNumbers[i].name?.toString());
          this.phones.at(i).get('contactNameEng')?.setValue(this.currentPhoneNumbers[i].nameEng?.toString());
          this.phones.at(i).get('contactNameIta')?.setValue(this.currentPhoneNumbers[i].nameIta?.toString());
          this.phones.at(i).get('contactNameSlo')?.setValue(this.currentPhoneNumbers[i].nameSlo?.toString());
          this.phones.at(i).get('contactNameSr')?.setValue(this.currentPhoneNumbers[i].nameSr?.toString());
          this.phones.at(i).get('contactNameBs')?.setValue(this.currentPhoneNumbers[i].nameBs?.toString());
        }
      }
    }

    this.inputForm.controls.phones.patchValue(this.phones.value);
    if (this.disableEditing) {
      this.inputForm.disable();
      this.inputForm.controls.phones.controls.forEach(control => {
        control.disable();
      })
    }
  }

  private setFormValues() {
    if (this.currentEventItem) {
      let formValue = {...this.currentEventItem};
      if (formValue.organiser) {
        formValue.organiser!.country = this.getCountry(this.currentEventItem?.organiser?.country?.shortCode);
      }
      this.inputForm.patchValue(formValue);
      if (this.currentEventItem?.organiser) {
        this.selectedImage = this.currentEventItem?.organiser.imageMedium;
      }
      this.inputForm.controls.measurementByStotinka.setValue(this.currentEventItem?.measurementByStotinka ?? false);
      this.subscriptions.push(
        this.inputForm.valueChanges.pipe(
          take(1)).subscribe(() => {
            this.inputForm.markAsTouched();
          }
        )
      );
    }
  }

  getCountry(callCode?: string): Country {
    return this.countries.filter((country: Country) => country.callCode === callCode)[0] ?? undefined;
  }

  get phones(): FormArray {
    return this.inputForm.controls.phones as FormArray;
  }

  getPhoneGroup(index: number): FormGroup {
    return this.phones.at(index) as FormGroup;
  }

  addPhone() {
    let phone = this.formBuilder.group({
      phone: ['', required],
      contactName: ['', required],
      contactNameEng: [''],
      contactNameIta: [''],
      contactNameSlo: [''],
      contactNameSr: [''],
      contactNameBs: ['']
    });
    this.subscriptions.push(phone.valueChanges.subscribe((_) => {
      this.inputForm.updateValueAndValidity()
    }));
    this.phones.push(phone);
    let newPhone: PhoneNumber = new PhoneNumber();
    this.currentPhoneNumbers.push(newPhone);
    this.inputForm.updateValueAndValidity();
  }

  deletePhone(index: number) {
    this.phones.removeAt(index);
    this.currentPhoneNumbers.splice(index, 1);
    this.inputForm.updateValueAndValidity();
  }

  private setPhoneNumber(element: string | undefined) {
    this.phones.at(0).get('phone')?.setValue(element);
    this.phones.at(0).get('contactName')?.setValue(this.currentEventItem?.organiser?.name);
    this.phones.at(0).get('contactNameEng')?.setValue(this.currentEventItem?.organiser?.name);
    this.phones.at(0).get('contactNameIta')?.setValue(this.currentEventItem?.organiser?.name);
    this.phones.at(0).get('contactNameSlo')?.setValue(this.currentEventItem?.organiser?.name);
    this.phones.at(0).get('contactNameSr')?.setValue(this.currentEventItem?.organiser?.name);
  }

  onSave() {
    if (!this.inputForm.valid || this.disableEditing) {
      this.markAllAsTouched(this.inputForm);
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.error.form"))
    } else {
      let eventDetails: EventItem = new EventItem();
      eventDetails.description = this.inputForm.value.description;
      eventDetails.descriptionEng = this.inputForm.value.descriptionEng;
      eventDetails.descriptionSlo = this.inputForm.value.descriptionSlo;
      eventDetails.descriptionIta = this.inputForm.value.descriptionIta;
      eventDetails.descriptionSr = this.inputForm.value.descriptionSr;
      eventDetails.descriptionBs = this.inputForm.value.descriptionBs;
      eventDetails.rules = this.inputForm.value.rules;
      eventDetails.rulesEng = this.inputForm.value.rulesEng;
      eventDetails.rulesSlo = this.inputForm.value.rulesSlo;
      eventDetails.rulesIta = this.inputForm.value.rulesIta;
      eventDetails.rulesSr = this.inputForm.value.rulesSr;
      eventDetails.rulesBs = this.inputForm.value.rulesBs;
      eventDetails.awardFund = this.inputForm.value.awardFund;
      eventDetails.awardFundEng = this.inputForm.value.awardFundEng;
      eventDetails.awardFundSlo = this.inputForm.value.awardFundSlo;
      eventDetails.awardFundIta = this.inputForm.value.awardFundIta;
      eventDetails.awardFundSr = this.inputForm.value.awardFundSr;
      eventDetails.awardFundBs = this.inputForm.value.awardFundBs;
      eventDetails.measurementByStotinka = this.inputForm.value.measurementByStotinka;
      eventDetails.infoEmail = this.inputForm.value.eMail;
      eventDetails.mediaContentVideoHor = this.inputForm.value.mediaContentVideoHor;
      eventDetails.mediaContentVideoVert = this.inputForm.value.mediaContentVideoVert;
      eventDetails.mediaContentHor = this.inputForm.value.mediaContentHor;
      eventDetails.mediaContentHorEng = this.inputForm.value.mediaContentHorEng;
      eventDetails.mediaContentHorIta = this.inputForm.value.mediaContentHorIta;
      eventDetails.mediaContentHorSlo = this.inputForm.value.mediaContentHorSlo;
      eventDetails.mediaContentHorSr = this.inputForm.value.mediaContentHorSr;
      eventDetails.mediaContentHorBs = this.inputForm.value.mediaContentHorBs;
      eventDetails.mediaContentVert = this.inputForm.value.mediaContentVert;
      eventDetails.mediaContentVertEng = this.inputForm.value.mediaContentVertEng;
      eventDetails.mediaContentVertIta = this.inputForm.value.mediaContentVertIta;
      eventDetails.mediaContentVertSlo = this.inputForm.value.mediaContentVertSlo;
      eventDetails.mediaContentVertSr = this.inputForm.value.mediaContentVertSr;
      eventDetails.mediaContentVertBs = this.inputForm.value.mediaContentVertBs;
      eventDetails.descriptionImage = this.inputForm.value.descriptionImage;
      eventDetails.rulesImage = this.inputForm.value.rulesImage;
      let currentPhoneNumbers: PhoneNumber[] = [];
      if (this.phones.length > 0) {
        for (let i: number = 0; i < this.phones.length; i++) {
          currentPhoneNumbers.push(new PhoneNumber(undefined, undefined,
              this.phones.at(i).get('phone')?.value,
              this.phones.at(i).get('contactName')?.value,
              this.phones.at(i).get('contactNameEng')?.value,
              this.phones.at(i).get('contactNameIta')?.value,
              this.phones.at(i).get('contactNameSr')?.value,
              this.phones.at(i).get('contactNameBs')?.value,
              this.phones.at(i).get('contactNameSlo')?.value));
        }
      }
      eventDetails.phoneNumbers = currentPhoneNumbers ?? undefined;

      if (this.currentEventItem?.id) {
        this.eventService.updateEventInfo(this.currentEventItem!.id!, eventDetails, () => {
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("app.common.success.event.info.data.edit"));
            this.eventService.findEventByIdProtected(new FetchEventByIdRequest(this.currentEventItem!.id!), true).subscribe((event: EventItem | null) => {
            this.eventService.currentEvent.next(event ?? undefined);
          });
        }, () => {
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("app.common.server.error"))
        })
      }
    }
  }


  rulesImageChange(image: string | undefined) {
    this.inputForm.controls.rulesImage.setValue(image);
  }

  descriptionImageChange(image: string | undefined) {
    this.inputForm.controls.descriptionImage.setValue(image);
  }

  protected readonly DESCRIPTION_MAX_SIZE_MB = EventInfoComponent.DESCRIPTION_MAX_SIZE_MB;
}
